import { DictionaryType } from '../api/dictionaryType.enum';
import { Dictionary, DictionaryItem, ObjectAccessor } from '../public_api';
import { ObservationImage } from './observationImage';
import { ObservationSummary } from './observationSummary';

export class ObservationDetails extends ObservationSummary {
  public static readonly KEYS = {
    ...ObservationSummary.KEYS,
    KEYWORDS: 'keywords',
    PROPERTIES: 'properties',
    IMAGES: 'images',
    IMAGE_OBJECTS: 'imageObjects', // added locally by constructObject
  };

  public static readonly requiredDictionaryTypes = [
    ...ObservationSummary.requiredDictionaryTypes,
    DictionaryType.KEYWORDS_USED,
    DictionaryType.PROPERTIES,
  ];

  public readonly images: Array<ObservationImage>;
  public readonly keywords: Array<DictionaryItem>;
  public readonly properties: Array<PropertyObject>;

  public static constructObject(
    source: Record<string, unknown>,
    dictionaries?: Array<Dictionary>,
  ): Promise<ObservationDetails> {
    // constructs the ObservationImage objects and adds them to the source object
    return Promise.all(
      ObjectAccessor.getArray(ObservationDetails.KEYS.IMAGES, source).map((image: Record<string, unknown>) =>
        ObservationImage.constructObject(image),
      ),
    ).then(
      (imageObjects: Array<ObservationImage>) =>
        super.constructObject(
          {
            ...source,
            imageObjects,
          },
          dictionaries,
        ) as Promise<ObservationDetails>,
    );
  }

  protected constructor(sourceObject: Record<string, unknown>, dictionaries?: Array<Dictionary>) {
    super(sourceObject, dictionaries);

    this.images = this._getValue(ObservationDetails.KEYS.IMAGE_OBJECTS) as Array<ObservationImage>;

    const keywords = this._getArray(ObservationDetails.KEYS.KEYWORDS);
    this.keywords =
      null == keywords
        ? []
        : this._getDictionary(DictionaryType.KEYWORDS_USED).getItems(
            keywords.map((keyword: Record<string, unknown>) => this._getString('keyword_code', keyword)),
          );

    const properties = this._getArray(ObservationDetails.KEYS.PROPERTIES);
    this.properties =
      null == properties
        ? []
        : properties
            .map((property: Record<string, unknown>) => {
              const dictItem = this._getDictionary(DictionaryType.PROPERTIES).getItem(
                this._getString('property_code', property),
              );
              return null == dictItem
                ? null
                : ({
                    property: dictItem,
                    value: this._getString('property_value', property),
                  } as PropertyObject);
            })
            .filter((item) => null != item); // filter nulls
  }
}

export interface PropertyObject {
  property: DictionaryItem;
  value: string;
}
